/**
 * PATH: packages/tenant-shared/src/components/Toast.tsx
 * FILE_ID: TENANTSHARED-COMPONENTS-TOAST-001
 * PURPOSE: Universal `<ToastProvider>` + `useToast()` for every tenant
 *          SPA on dashboard.quotech.ai. Lifted from the byte-identical
 *          apps/{newmark,sam}/src/shared/components/Toast.tsx so the
 *          tenant tree, the @quotech/email-hub package (currently a
 *          private copy slated for consolidation), and the new
 *          @quotech/settings-hub package all consume the SAME React
 *          Context object.
 *
 * WHY:     React Context identity matters: a Provider mounted from
 *          module A and a `useContext` call from module B (a copy of
 *          the same file) read DIFFERENT contexts and the consumer
 *          silently sees the default no-op. The tenant tree mounts
 *          `<ToastProvider>` once at apps/<tenant>/main.tsx; lifting
 *          the module here means every consumer (including extracted
 *          feature packages) reads through the same `ToastContext`.
 *
 * FLOW:
 *   ┌──────────────────┐   ┌────────────────────────┐   ┌──────────────┐
 *   │ tenant boot      │ → │ ToastProvider wraps    │ → │ packages +   │
 *   │ (apps/<t>/main)  │   │ the React tree once    │   │ tenant tree  │
 *   │                  │   │                        │   │ both useToast│
 *   └──────────────────┘   └────────────────────────┘   └──────────────┘
 *
 * RELATED:
 *   - apps/{newmark,sam}/src/shared/components/Toast.tsx — thin shims
 *     that `export * from '@quotech/tenant-shared/components/Toast'`.
 *   - packages/settings-hub/src/_internal/runtime.ts — re-exports
 *     `useToast` from this module so the lifted SettingsPage body's
 *     `import { useToast } from '...'` keeps semantically pointing at
 *     the same Context the tenant Provider mounts.
 *   - packages/email-hub/src/_internal/components/Toast.tsx — internal
 *     copy that pre-dates this lift; consolidating it into this module
 *     is a follow-up MR.
 */

import { useState, useCallback, createContext, useContext, useEffect } from 'react'
import { cn } from '../lib/utils'
import { CheckCircle, XCircle, AlertTriangle, X, Info } from 'lucide-react'
import { NOTIFY_EVENT, pushNotification } from '../stores/notificationStore'
import { getToastVariantClasses } from '../lib/toastVariants'

/**
 * Coerce ANY value to a render-safe string. Mirror of the helper in
 * NotificationBell — duplicated to keep Toast.tsx free of
 * cross-component imports. See that file for the bug history (jake
 * 2026-05-04 blank-screen incident).
 */
function toSafeText(value: unknown, maxLen = 200): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    const s = JSON.stringify(value)
    return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s
  } catch {
    return '[unrenderable]'
  }
}

type ToastType = 'success' | 'error' | 'warning' | 'info'

/** Single inline action rendered as a button inside the toast (e.g.
 *  "Undo" after a kanban card archive — UAT P1-b). Clicking runs the
 *  callback and dismisses the toast. */
export interface ToastAction {
  label: string
  onClick: () => void
}

interface Toast {
  id: number
  message: string
  type: ToastType
  action?: ToastAction
}

interface ToastContextType {
  /**
   * Show an ephemeral bottom-right toast. By default, the toast is
   * ALSO recorded in the top-nav notification center so the user can
   * revisit it via the bell icon. Set ``persist: false`` for trivial
   * toasts (e.g. "Copied to clipboard") that don't deserve a bell row.
   */
  toast: (
    message: string,
    type?: ToastType,
    options?: {
      persist?: boolean
      source?: string
      title?: string
      action?: ToastAction
      /** Override auto-dismiss (ms). Undo toasts pass their full undo window. */
      durationMs?: number
    },
  ) => void
}

const ToastContext = createContext<ToastContextType>({ toast: () => {} })

export function useToast() {
  return useContext(ToastContext)
}

let nextId = 0

const ICON_MAP = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
}

// Container classes per variant come from the canonical shared
// palette in `../lib/toastVariants`. The palette is OPAQUE by
// construction — see BUG-2026-05-20-TOAST-CONTRAST. Do NOT add a
// local override here; edit the shared module so every Toast in
// the repo (universal, tenant-shared, email-hub) stays in lockstep.

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback(
    (
      message: string,
      type: ToastType = 'success',
      options?: {
        persist?: boolean
        source?: string
        title?: string
        action?: ToastAction
        durationMs?: number
      },
    ) => {
      const id = nextId++
      setToasts((prev) => [...prev, { id, message, type, action: options?.action }])
      // Action toasts (Undo etc.) get a longer window — 4s proved too
      // tight to read + reach the button in QA. Callers with a product
      // timing contract (reader undo window) pass durationMs explicitly.
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
      }, options?.durationMs ?? (options?.action ? 6500 : 4000))
      // Mirror into the persistent notification center unless explicitly
      // opted out. Default ON so callers don't have to remember.
      if (options?.persist !== false) {
        pushNotification({
          title: options?.title ?? message.slice(0, 80),
          body: options?.title ? message : undefined,
          kind: type,
          source: options?.source ?? 'app',
        })
      }
    },
    [],
  )

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  // Subscribe to the global ``<tenant>.notify`` bus so any notification
  // pushed via ``notify(...)`` from a non-React module also surfaces
  // as a bottom-right toast. Without this, ``notify()`` calls would
  // land in the bell silently.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { title: string; body?: string; kind?: ToastType }
        | undefined
      if (!detail || typeof detail.title !== 'string') return
      const id = nextId++
      const message = detail.body ? `${detail.title} — ${detail.body}` : detail.title
      setToasts((prev) => [...prev, { id, message, type: detail.kind ?? 'info' }])
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
      }, 4000)
    }
    window.addEventListener(NOTIFY_EVENT, handler)
    return () => window.removeEventListener(NOTIFY_EVENT, handler)
  }, [])

  return (
    <ToastContext.Provider value={{ toast: addToast }}>
      {children}
      {/* COMPACT-TOAST (2026-05-22) — operator complaint: success
       *  toasts on small kanban gestures (nest, promote, archive)
       *  felt much too big. Width tightened from `max-w-sm`
       *  (24 rem) to `max-w-[280px]` (17.5 rem) and the chrome
       *  scaled to match: `py-2 px-3` and `text-[12.5px]`.
       *  The error/warning palette is unchanged — only the
       *  metrics shrink. */}
      {/* z-[120] + dynamic bottom offset — keep in lockstep with the
          email-hub and ui Toast twins (UX audit 2026-06, Layout #5/#6):
          toasts top the overlay scale and lift above the MobileDock /
          Email Hub bottom bar via the published height vars. */}
      <div
        className="fixed right-4 z-[120] flex flex-col gap-1.5 max-w-[280px]"
        style={{
          bottom:
            'calc(1rem + var(--mobile-dock-h, 0px) + var(--email-hub-bottom-bar-h, 0px))',
        }}
      >
        {toasts.map(t => {
          const Icon = ICON_MAP[t.type]
          return (
            <div
              key={t.id}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-lg border shadow-md',
                'animate-slide-up',
                getToastVariantClasses(t.type),
              )}
              role="status"
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              {/*
                Render the toast text via ``toSafeText`` so an
                accidental object payload (legacy callers, future
                regressions) never crashes the toast container with
                React #31. See NotificationBell.toSafeText for the
                full bug history. Toasts live above the
                AppErrorBoundary, so a render crash here would blank
                the whole app — defensive coercion is mandatory.
              */}
              <p className="text-[12.5px] leading-snug flex-1">{toSafeText(t.message)}</p>
              {t.action && (
                <button
                  onClick={() => {
                    // Dismiss FIRST so a throwing action can't strand
                    // a dead Undo button on screen.
                    removeToast(t.id)
                    t.action!.onClick()
                  }}
                  className="shrink-0 rounded px-1.5 py-0.5 text-[11.5px] font-semibold underline underline-offset-2 hover:opacity-80"
                >
                  {toSafeText(t.action.label, 24)}
                </button>
              )}
              <button
                onClick={() => removeToast(t.id)}
                className="shrink-0 opacity-50 hover:opacity-100 -mr-0.5"
                aria-label="Dismiss"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}
